import { INVALID_INPUT } from '~/consts/customerCommunication/inputErrors';

import { isScheduleIsCustomScheduleType } from './heroPlannerScheduleUtils';

export function isInvalidInput(input?: string): boolean {
  // no input means it's valid
  if (!input || input.trim() === '') {
    return false;
  }

  return INVALID_INPUT.some((invalidInput) => input.includes(invalidInput));
}

export function getWarningColourTheme(input?: string, maxChar?: number): string {
  if (!input || input?.trim() === '' || !maxChar) {
    return 'default';
  }
  if (input.length > maxChar) {
    return 'error.main';
  } else if (input.length > maxChar - 10) {
    return 'warning.light';
  } else {
    return 'default';
  }
}

/**
 * Checks if a URL is a valid URL for the specified brand.
 *
 * @param brand - The brand identifier (e.g., 'luxuryescapes', 'scoopontravel')
 * @param url - The URL to validate
 * @returns true if the URL is valid for the brand, false otherwise
 *
 * @example
 * isValidUrl('luxuryescapes', 'https://luxuryescapes.com')
 * // returns true
 *
 * isValidUrl('luxuryescapes', 'https://www.luxuryescapes.com/au')
 * // returns true
 *
 * isValidUrl('scoopontravel', 'https://deals.scoopontravel.com/au/destinations')
 * // returns true
 *
 * isValidUrl('luxuryescapes', 'https://scoopontravel.com')
 */
export function isValidUrl(brand: string, url: string): boolean {
  if (!url || url.trim() === '') {
    return true;
  }

  // trim travel from the end of the brand
  // TODO: MOVE THIS TO A MAP OF BRANDS AND THEIR URLS
  const brandString = brand.replace('travel', '');

  const tldPart = brandString === 'luxuryescapes' ? '(?:com|co)' : '(?:com|co)(?:\\.(?:au|nz))?';

  const brandUrlRegex = new RegExp(`^https?:\\/\\/(?:[\\w-]+\\.)*${brandString}\\.${tldPart}(?:\\/[^\\s]*)?$`);

  return brandUrlRegex.test(url);
}

type PushScheduleTextField = { title?: string; subtitle?: string; message?: string };
type PushScheduleLinkField = { link?: string };

export const getPushScheduleTextFields = (pushSchedules: Array<PushScheduleTextField>) =>
  pushSchedules.flatMap((s) => [s.title, s.subtitle, s.message]);

export const getPushScheduleLinkFields = (pushSchedules: Array<PushScheduleLinkField>) =>
  pushSchedules.map((s) => s.link);

/**
 * Extracts all text fields from a schedule based on its type.
 * Custom schedules only have pushSchedules, while HeroPlanner schedules also have root-level push fields.
 */
export function getScheduleTextFields(
  unsaved: CustomerCommunication.UnsavedHeroPlannerSchedule | CustomerCommunication.UnsavedCustomSchedule,
): Array<string | undefined> {
  const pushScheduleFields = getPushScheduleTextFields(unsaved.pushSchedules ?? []);

  if (isScheduleIsCustomScheduleType(unsaved)) {
    return pushScheduleFields;
  }

  const heroPlannerSchedule = unsaved;
  return [
    heroPlannerSchedule.pushTitle,
    heroPlannerSchedule.pushSubtitle,
    heroPlannerSchedule.pushMessage,
    ...pushScheduleFields,
  ];
}

/**
 * Extracts all link fields from a schedule based on its type.
 * Custom schedules only have pushSchedules, while HeroPlanner schedules also have root-level pushLink.
 */
export function getScheduleLinkFields(
  unsaved: CustomerCommunication.UnsavedHeroPlannerSchedule | CustomerCommunication.UnsavedCustomSchedule,
): Array<string | undefined> {
  const pushScheduleLinks = getPushScheduleLinkFields(unsaved.pushSchedules ?? []);

  if (isScheduleIsCustomScheduleType(unsaved)) {
    return pushScheduleLinks;
  }

  const heroPlannerSchedule = unsaved;
  return [heroPlannerSchedule.pushLink, ...pushScheduleLinks];
}

export const VALIDATION_ERRORS = {
  INVALID_TEXT: 'Invalid input. Please check the title, subtitle, or message.',
  INVALID_URL: 'Invalid input. Please enter a valid Brand URL.',
} as const;

/**
 * Validates a schedule's push content fields and returns an error message if invalid.
 * Returns null if validation passes.
 */
export function validateSchedulePushContent(
  unsaved: CustomerCommunication.UnsavedHeroPlannerSchedule | CustomerCommunication.UnsavedCustomSchedule,
): string | null {
  const textFields = getScheduleTextFields(unsaved);
  if (textFields.some(isInvalidInput)) {
    return VALIDATION_ERRORS.INVALID_TEXT;
  }

  const linkFields = getScheduleLinkFields(unsaved);
  if (linkFields.some((link) => !isValidUrl(unsaved.brandId, link))) {
    return VALIDATION_ERRORS.INVALID_URL;
  }

  return null;
}
